import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowUpward,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { env } from "../../../utilities/function";
import { getCookie } from "../../../utilities/cookies";
import ProductOfferForm from "./ProductOfferForm";

const API_BASE_URL = env("SERVER") + "/api";

const fetchOffers = async (page = 1, limit = 100) => {
  const accessToken = getCookie("accessToken");
  const response = await axios.get(`${API_BASE_URL}/product-offers?page=${page}&limit=${limit}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const deleteOffer = async (id) => {
  const accessToken = getCookie("accessToken");
  return await axios.delete(`${API_BASE_URL}/product-offers/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const toggleOfferStatus = async ({ id, isEnabled }) => {
  const accessToken = getCookie("accessToken");
  return await axios.patch(
    `${API_BASE_URL}/product-offers/${id}/toggle`,
    { isEnabled },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
};

const ProductOffers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const [page] = useState(1);
  const [limit] = useState(100);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    data: offersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["productOffers", page, limit],
    queryFn: () => fetchOffers(page, limit),
    keepPreviousData: true,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOffer,
    onSuccess: () => {
      queryClient.invalidateQueries(["productOffers"]);
      setSnackbar({
        open: true,
        message: "Offer deleted successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting offer: ${error.message}`,
        severity: "error",
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: toggleOfferStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(["productOffers"]);
      setSnackbar({
        open: true,
        message: "Offer status updated!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating status: ${error.message}`,
        severity: "error",
      });
    },
  });

  const handleCreate = () => {
    setEditingOffer(null);
    setDialogOpen(true);
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setDialogOpen(true);
  };

  const handleDelete = (id) => {
    setOfferToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (offerToDelete) {
      deleteMutation.mutate(offerToDelete);
      setDeleteConfirmOpen(false);
      setOfferToDelete(null);
    }
  };

  const handleToggle = (offer) => {
    toggleMutation.mutate({ id: offer._id, isEnabled: !offer.isEnabled });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading offers: {error.message}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(4),
          borderRadius: 2,
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={"bold"} gutterBottom>
            Product Offers Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage dynamic landing pages for product offers.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          size={isMobile ? "small" : "medium"}
        >
          Create Offer
        </Button>
      </Box>

      <Card
        sx={{
          backgroundColor: "transparent",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Offer Slug</TableCell>
                  <TableCell>Hero Title</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offersData?.data?.offers?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No offers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  offersData?.data?.offers?.map((offer) => (
                    <TableRow key={offer._id}>
                      <TableCell>{offer.name}</TableCell>
                      <TableCell>{offer.slug}</TableCell>
                      <TableCell>{offer.hero?.title}</TableCell>
                      <TableCell>
                        {new Date(offer.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={offer.isEnabled ? "Active" : "Inactive"}
                          size="small"
                          color={offer.isEnabled ? "success" : "default"}
                          variant={offer.isEnabled ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => window.open(`https://wurknest.com/offers/${offer.slug}`, "_blank")}
                            color="info"
                            title="Open in new tab"
                          >
                           <ArrowUpward />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleToggle(offer)}
                            color={offer.isEnabled ? "success" : "default"}
                          >
                            {offer.isEnabled ? <ViewIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(offer)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(offer._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <ProductOfferForm
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingOffer(null);
        }}
        offer={editingOffer}
        onSuccess={() => {
          queryClient.invalidateQueries(["productOffers"]);
          setDialogOpen(false);
          setEditingOffer(null);
          setSnackbar({
            open: true,
            message: editingOffer
              ? "Offer updated successfully!"
              : "Offer created successfully!",
            severity: "success",
          });
        }}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Offer?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this offer? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductOffers;
